import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';

import type { IWeekBlocks } from '@/types/block';

import { useAppSelector } from '@/hooks';
import { ContractRequirementsWidget, Sidebar, WeeksRangePicker } from '@/components/common';
import { ToggleAvailableShifts } from '@/components/agent/my-schedule';
import { SidebarSchedules } from '@/store/enums';
import { SidebarSchedulesPicker } from '@/components/common/SidebarSchedulesPicker';
import { selectSchedulesList, selectSelectedSchedulesIds } from '@/store/selectors/scheduleSelectors';

const myScheduleSidebarSelector = createSelector(
  selectSchedulesList,
  selectSelectedSchedulesIds,
  (schedulesList, selectedSchedulesIds) => ({
    schedulesList,
    selectedSchedulesIds,
  }),
);

const MyScheduleSidebar = ({ weekBlocks }: { weekBlocks: IWeekBlocks }): React.ReactElement => {
  const { t } = useTranslation();
  const { schedulesList, selectedSchedulesIds } = useAppSelector(myScheduleSidebarSelector);

  const countSelectedSchedules = useCallback((): string => {
    const total = schedulesList.length;
    const selected = selectedSchedulesIds.length;

    return `${selected}/${total}`;
  }, [schedulesList, selectedSchedulesIds]);

  return (
    <Sidebar title={t`mySchedule.leftPanel.title`}>
      <ToggleAvailableShifts weekBlocks={weekBlocks} />
      <WeeksRangePicker className="scheduling" />
      <SidebarSchedulesPicker
        name={t`commonComponents.schedulesPicker.labelSchedules`}
        rightText={countSelectedSchedules()}
        schedulesList={schedulesList}
        activeSchedulesLabel={countSelectedSchedules()}
        hours={SidebarSchedules.default}
      />
      <ContractRequirementsWidget weekBlocks={weekBlocks} />
    </Sidebar>
  );
};

export { MyScheduleSidebar };
